import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { getComparisonBySlug } from '@/config/comparisons.config';

interface ComparisonPageProps {
    showBanner: () => void;
}

export const ComparisonPage: React.FC<ComparisonPageProps> = ({ showBanner }) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat(slug);
    const comparison = slug ? getComparisonBySlug(slug) : undefined;

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
                <Footer />
            </>
        );
    }

    if (!comparison) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto text-center text-white">
                        <h1 className="text-2xl font-bold mb-4">Comparison Not Found</h1>
                        <p className="text-gray-400 mb-6">This comparison page doesn't exist yet.</p>
                        <button
                            onClick={() => navigate('/compare')}
                            className="px-6 py-2 bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all"
                        >
                            Browse Comparisons
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const { vehicle1, vehicle2 } = comparison;

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: comparison.commonQuestions.map(q => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `Drivers comparing the ${vehicle1.name} and ${vehicle2.name} often evaluate ${comparison.categoryLabel.toLowerCase()} options based on performance, reliability, and features. The best choice depends on individual needs and preferences.`,
            },
        })),
    };

    const h1Parts = comparison.h1.split(' — ');
    const h1Name = h1Parts[0];
    const h1Tagline = h1Parts.slice(1).join(' — ');

    return (
        <>
            <SchemaMarkup schema={faqSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            {/* H1 */}
                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6">
                                {h1Name}
                                {h1Tagline && (
                                    <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                        {h1Tagline}
                                    </span>
                                )}
                            </h1>

                            {/* Intro */}
                            <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6">
                                The {vehicle1.name} and {vehicle2.name} are two of the most popular {comparison.categoryLabel.toLowerCase()}s in North America. Both vehicles are widely used for {vehicle1.commonUses.toLowerCase()}. Drivers researching {comparison.categoryLabel.toLowerCase()}s often compare the {vehicle1.name} and {vehicle2.name} when deciding which vehicle offers the best combination of performance, reliability, and features. {comparison.introExtra}
                            </p>

                            {/* Quick suggestions */}
                            <QuickSuggestionsBlock
                                topLine="Not sure where to start?"
                                title={`Ask anything about the ${vehicle1.name} vs ${vehicle2.name}.`}
                                suggestions={comparison.quickSuggestions}
                                onSend={sendMessage}
                            />

                            {/* Quick Comparison Table */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Quick Comparison
                                </h2>
                                <div className="overflow-x-auto rounded-xl border border-gray-600">
                                    <table className="w-full text-sm text-gray-300">
                                        <thead>
                                            <tr className="bg-gray-800">
                                                <th className="text-left px-3 py-2 text-gray-400 font-medium w-1/3">Category</th>
                                                <th className="text-left px-3 py-2 text-white font-semibold">{vehicle1.name}</th>
                                                <th className="text-left px-3 py-2 text-white font-semibold">{vehicle2.name}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700">
                                            <tr className="bg-[#1a1a1a]">
                                                <td className="px-3 py-2 text-gray-400">Vehicle Type</td>
                                                <td className="px-3 py-2">{vehicle1.vehicleType}</td>
                                                <td className="px-3 py-2">{vehicle2.vehicleType}</td>
                                            </tr>
                                            <tr className="bg-gray-900">
                                                <td className="px-3 py-2 text-gray-400">Manufacturer</td>
                                                <td className="px-3 py-2">{vehicle1.manufacturer}</td>
                                                <td className="px-3 py-2">{vehicle2.manufacturer}</td>
                                            </tr>
                                            <tr className="bg-[#1a1a1a]">
                                                <td className="px-3 py-2 text-gray-400">Common Uses</td>
                                                <td className="px-3 py-2">{vehicle1.commonUses}</td>
                                                <td className="px-3 py-2">{vehicle2.commonUses}</td>
                                            </tr>
                                            <tr className="bg-gray-900">
                                                <td className="px-3 py-2 text-gray-400">Drivetrain</td>
                                                <td className="px-3 py-2">{vehicle1.drivetrain}</td>
                                                <td className="px-3 py-2">{vehicle2.drivetrain}</td>
                                            </tr>
                                            <tr className="bg-[#1a1a1a]">
                                                <td className="px-3 py-2 text-gray-400">Key Competitors</td>
                                                <td className="px-3 py-2">{vehicle1.keyCompetitors}</td>
                                                <td className="px-3 py-2">{vehicle2.keyCompetitors}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Key Differences */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Key Differences Between the {vehicle1.name} and {vehicle2.name}
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2 mb-3">
                                    {comparison.keyDifferenceItems.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                                <p className="text-sm sm:text-base text-gray-300 mb-1">Drivers comparing these vehicles often ask:</p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    {comparison.commonQuestions.map((q, i) => (
                                        <li key={i}>{q}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Overview */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Overview
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">
                                    The {vehicle1.name} and {vehicle2.name} compete in the {comparison.categoryLabel.toLowerCase()} segment. {comparison.categoryLabel}s are designed for:
                                </p>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {comparison.overviewBullets.map((b, i) => (
                                        <li key={i}>{b}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Performance */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Performance and Capability
                                </h2>

                                <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                    {vehicle1.name} Performance
                                </h3>
                                <p className="text-sm sm:text-base text-gray-300 mb-1">{vehicle1.performance}</p>
                                <p className="text-sm sm:text-base text-gray-300 mb-1">Drivers often choose the {vehicle1.name} for:</p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2 mb-3">
                                    {vehicle1.bestFor.map((b, i) => <li key={i}>{b}</li>)}
                                </ul>

                                <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                    {vehicle2.name} Performance
                                </h3>
                                <p className="text-sm sm:text-base text-gray-300 mb-1">{vehicle2.performance}</p>
                                <p className="text-sm sm:text-base text-gray-300 mb-1">Drivers often choose the {vehicle2.name} for:</p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    {vehicle2.bestFor.map((b, i) => <li key={i}>{b}</li>)}
                                </ul>
                            </div>

                            {/* Interior and Technology */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Interior and Technology
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">
                                    Modern {comparison.categoryLabel.toLowerCase()}s include a range of interior features and driver assistance systems. Common features in both vehicles may include:
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>infotainment displays</li>
                                    <li>smartphone connectivity</li>
                                    <li>driver safety technology</li>
                                    <li>comfortable seating</li>
                                </ul>
                            </div>

                            {/* Reliability */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Reliability and Ownership
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">
                                    Both the {vehicle1.name} and {vehicle2.name} are widely researched for long-term ownership. Drivers often compare:
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>reliability reputation</li>
                                    <li>maintenance costs</li>
                                    <li>long-term durability</li>
                                    <li>resale value</li>
                                </ul>
                            </div>

                            {/* Which Is Better */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Which {comparison.categoryLabel} Is Better for Different Drivers
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">{vehicle1.whichIsBetter}</p>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">{vehicle2.whichIsBetter}</p>
                                <p className="text-sm sm:text-base text-gray-300">{comparison.conclusion}</p>
                            </div>

                            {/* Related Vehicles */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Explore Related {comparison.categoryLabel}s
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {comparison.relatedVehicles.map((v, i) => (
                                        <li key={i}>
                                            <Link to={v.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                {v.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Other Comparisons */}
                            {comparison.otherComparisons.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Compare Other {comparison.categoryLabel}s
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {comparison.otherComparisons.map((c, i) => (
                                            <li key={i}>
                                                <Link to={c.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {c.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Dealerships */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Dealerships That Sell These Vehicles
                                </h2>
                                {comparison.dealerships.length > 0 ? (
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {comparison.dealerships.map((d, i) => (
                                            <li key={i}>
                                                <button
                                                    onClick={() => navigate(`/dealerships/${d.slug}`)}
                                                    className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                                >
                                                    {d.name}
                                                </button>
                                                {' '}&mdash; {d.city}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm sm:text-base text-gray-300">
                                        Contact local dealerships to check availability near you.
                                    </p>
                                )}
                            </div>

                            {/* Research Topics */}
                            {comparison.researchTopics.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Research {comparison.categoryLabel}s Before Choosing a Model
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {comparison.researchTopics.map((t, i) => (
                                            <li key={i}>
                                                <Link to={t.path} className="text-cyan-400 hover:text-cyan-300 underline transition-colors">
                                                    {t.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </article>

                        {/* Chat messages */}
                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
